import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetFixSessionQueryResponse, GetFixSessionPathParams, GetFixSession404 } from "../types/GetFixSession";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetFixSessionClient = typeof client<GetFixSessionQueryResponse, GetFixSession404, never>;
type GetFixSession = {
    data: GetFixSessionQueryResponse;
    error: GetFixSession404;
    request: never;
    pathParams: GetFixSessionPathParams;
    queryParams: never;
    headerParams: never;
    response: GetFixSessionQueryResponse;
    client: {
        parameters: Partial<Parameters<GetFixSessionClient>[0]>;
        return: Awaited<ReturnType<GetFixSessionClient>>;
    };
};
export const getFixSessionQueryKey = (clientCode: GetFixSessionPathParams["clientCode"], fixSessionId: GetFixSessionPathParams["fixSessionId"]) => [{ url: "/api/clients/:clientCode/fix-sessions/:fixSessionId", params: { clientCode: clientCode, fixSessionId: fixSessionId } }] as const;
export type GetFixSessionQueryKey = ReturnType<typeof getFixSessionQueryKey>;
export function getFixSessionQueryOptions(clientCode: GetFixSessionPathParams["clientCode"], fixSessionId: GetFixSessionPathParams["fixSessionId"], options: GetFixSession["client"]["parameters"] = {}) {
    const queryKey = getFixSessionQueryKey(clientCode, fixSessionId);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetFixSession["data"], GetFixSession["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/fix-sessions/${fixSessionId}`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a single fix session for a given client and fix session id.
 * @summary Get fix session
 * @link /api/clients/:clientCode/fix-sessions/:fixSessionId
 */
export function useGetFixSession<TData = GetFixSession["response"], TQueryData = GetFixSession["response"], TQueryKey extends QueryKey = GetFixSessionQueryKey>(clientCode: GetFixSessionPathParams["clientCode"], fixSessionId: GetFixSessionPathParams["fixSessionId"], options: {
    query?: Partial<QueryObserverOptions<GetFixSession["response"], GetFixSession["error"], TData, TQueryData, TQueryKey>>;
    client?: GetFixSession["client"]["parameters"];
} = {}): UseQueryResult<TData, GetFixSession["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getFixSessionQueryKey(clientCode, fixSessionId);
    const query = useQuery({
        ...getFixSessionQueryOptions(clientCode, fixSessionId, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetFixSession["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getFixSessionSuspenseQueryKey = (clientCode: GetFixSessionPathParams["clientCode"], fixSessionId: GetFixSessionPathParams["fixSessionId"]) => [{ url: "/api/clients/:clientCode/fix-sessions/:fixSessionId", params: { clientCode: clientCode, fixSessionId: fixSessionId } }] as const;
export type GetFixSessionSuspenseQueryKey = ReturnType<typeof getFixSessionSuspenseQueryKey>;
export function getFixSessionSuspenseQueryOptions(clientCode: GetFixSessionPathParams["clientCode"], fixSessionId: GetFixSessionPathParams["fixSessionId"], options: GetFixSession["client"]["parameters"] = {}) {
    const queryKey = getFixSessionSuspenseQueryKey(clientCode, fixSessionId);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetFixSession["data"], GetFixSession["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/fix-sessions/${fixSessionId}`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a single fix session for a given client and fix session id.
 * @summary Get fix session
 * @link /api/clients/:clientCode/fix-sessions/:fixSessionId
 */
export function useGetFixSessionSuspense<TData = GetFixSession["response"], TQueryKey extends QueryKey = GetFixSessionSuspenseQueryKey>(clientCode: GetFixSessionPathParams["clientCode"], fixSessionId: GetFixSessionPathParams["fixSessionId"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetFixSession["response"], GetFixSession["error"], TData, TQueryKey>>;
    client?: GetFixSession["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetFixSession["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getFixSessionSuspenseQueryKey(clientCode, fixSessionId);
    const query = useSuspenseQuery({
        ...getFixSessionSuspenseQueryOptions(clientCode, fixSessionId, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetFixSession["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}